import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Table, Spinner, Badge, Button, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import Breadcrumbs from '../../components/breadcrumbs';
import Paginations from '../../components/pagination';
import DeleteConfirmationModal from '../../components/delete-confirmation-modal';
import { FaTimes, FaChevronDown, FaChevronRight, FaEdit } from 'react-icons/fa';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Select from 'react-select';

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'video', 'image'],
    [{ align: [] }],
    ['clean'],
  ],
};

const quillFormats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'link', 'video', 'image', 'align',
];

const SavedCourses = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [paginationData, setPaginationData] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    total_pages: 1,
  });
  const [showOutlineModal, setShowOutlineModal] = useState(false);
  const [selectedOutline, setSelectedOutline] = useState(null);
  const [outlineLoading, setOutlineLoading] = useState(false);
  const [expandedUnits, setExpandedUnits] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editType, setEditType] = useState(null);
  const [editData, setEditData] = useState({ title: '', description: '', duration: '' });
  const [editLoading, setEditLoading] = useState(false);
  const [originalEditData, setOriginalEditData] = useState({ title: '', description: '', duration: '' });
  const [editCourseData, setEditCourseData] = useState({ course_id: null, course_title: '' });
  const [editCourseLoading, setEditCourseLoading] = useState(false);
  const [originalCourseTitle, setOriginalCourseTitle] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteCourseId, setDeleteCourseId] = useState(null);
  const [deletingCourse, setDeletingCourse] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importCourseId, setImportCourseId] = useState(null);
  const [importingCourse, setImportingCourse] = useState(false);
  const [showSelectCourseModal, setShowSelectCourseModal] = useState(false);
  const [selectedRowCourse, setSelectedRowCourse] = useState(null);
  const [selectedPart, setSelectedPart] = useState('');
  const [catArray, setCatArray] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchCourses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/standards-wizard/completed-courses`, {
        params: { 
          page, 
          per_page: 20, 
          search: debouncedSearchText?.trim() || ''
        },
      });

      if (response.data.success) {
        setCourses(response.data.data.courses || []);
        setPaginationData(response.data.data.pagination || { page: 1, per_page: 20, total: 0, total_pages: 1 });
      }
    } catch (error) {
      toast.error('Failed to fetch courses');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearchText]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const fetchOutline = async (courseId) => {
    setOutlineLoading(true);
    setExpandedUnits([]);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/standards-wizard/outline`, {
        params: { course_id: courseId },
      });

      if (response.data.success) {
        setSelectedOutline({ ...response.data.data, course_id: courseId });
        setEditCourseData({ course_id: courseId, course_title: response.data.data.course_title });
        setOriginalCourseTitle(response.data.data.course_title);
        setShowOutlineModal(true);
      }
    } catch (error) {
      toast.error('Failed to fetch outline');
    } finally {
      setOutlineLoading(false);
    }
  };

  const toggleUnit = (unitNumber) => {
    setExpandedUnits(prev => 
      prev.includes(unitNumber) ? prev.filter(u => u !== unitNumber) : [...prev, unitNumber]
    );
  };

  const handleEdit = (type, item) => {
    console.log('Edit clicked:', type, item);
    setEditType(type);
    if (type === 'unit') {
      const data = {
        id: item.unit_id,
        title: item.unit_title,
        description: item.unit_description,
        duration: item.duration_weeks || ''
      };
      setEditData(data);
      setOriginalEditData(data);
    } else {
      const data = {
        id: item.lesson_id,
        title: item.lesson_title,
        description: item.lesson_description,
        duration: item.duration_days || ''
      };
      setEditData(data);
      setOriginalEditData(data);
    }
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    setEditLoading(true);
    try {
      const payload = editType === 'unit' 
        ? { unit_id: editData.id, title: editData.title, description: editData.description, duration: parseInt(editData.duration) || 0 }
        : { lesson_id: editData.id, title: editData.title, description: editData.description, duration: parseInt(editData.duration) || 0 };

      const response = await axios.put(`${process.env.REACT_APP_API_URL}/standards-wizard/outline-item`, payload);

      if (response.data.success) {
        toast.success(response.data.message);
        setShowEditModal(false);
        fetchOutline(selectedOutline.course_id);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update');
    } finally {
      setEditLoading(false);
    }
  };

  const handleSaveCourseEdit = async () => {
    setEditCourseLoading(true);
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/standards-wizard/course`, {
        course_id: editCourseData.course_id,
        course_title: editCourseData.course_title
      });

      if (response.data.success) {
        toast.success('Course title updated successfully');
        setSelectedOutline({ ...selectedOutline, course_title: editCourseData.course_title });
        fetchCourses();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update course');
    } finally {
      setEditCourseLoading(false);
    }
  };

  const handleDeleteCourse = async (courseId) => {
    setDeleteCourseId(courseId);
    setShowDeleteModal(true);
  };

  const confirmDeleteCourse = async () => {
    setDeletingCourse(true);
    try {
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/standards-wizard/course`, {
        params: { course_id: deleteCourseId }
      });

      if (response.data.success) {
        toast.success('Course deleted successfully');
        setShowDeleteModal(false);
        fetchCourses();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete course');
    } finally {
      setDeletingCourse(false);
    }
  };

  const handleImportCourse = (course) => {
    setSelectedRowCourse(course);
    setSelectedPart('part 1');
    setSelectedCategory(null);
    setShowSelectCourseModal(true);
  };

  const getCatArray = () => {
    axios
      .post(
        'https://' +
          process.env.REACT_APP_LMS_URL +
          '/webservice/rest/server.php?wstoken=' +
          process.env.REACT_APP_LMS_TOKEN +
          '&wsfunction=local_course_service_get_category_list&moodlewsrestformat=json'
      )
      .then((response) => {
        if (response.data) {
          const { data } = response.data;
          const formattedOptions = (data || []).map((item) => ({
            value: item.Category_ID,
            label: item.Category_Name,
          }));
          setCatArray(formattedOptions);
        }
      })
      .catch(() => {
        toast.error('Failed to fetch categories');
      });
  };

  const handleSelectCourseImport = () => {
    if (!selectedCategory) {
      toast.error('Please select a category');
      return;
    }
    const isFullYear = selectedRowCourse?.duration?.toLowerCase() === 'full year';
    if (isFullYear && !selectedPart) {
      toast.error('Please select Part 1 or Part 2');
      return;
    }
    setImportCourseId(selectedCategory.value);
    setShowSelectCourseModal(false);
    setShowImportModal(true);
  };

  const confirmImportCourse = async () => {
    setImportingCourse(true);
    try {
      const isFullYear = selectedRowCourse?.duration?.toLowerCase() === 'full year';
      const payload = {
        id: selectedRowCourse.course_id,
        category_id: parseInt(selectedCategory?.value),
        part: isFullYear ? selectedPart : ''
      };
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/standards-wizard/scorm-import`, payload);

      if (response.data.success) {
        toast.success(response.data.message || 'Course imported successfully');
        setShowImportModal(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to import course');
    } finally {
      setImportingCourse(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  return (
    <Container fluid className="p-4">
      <Breadcrumbs />
      <p className="section-title mb-4">Saved Courses</p>

      <Row className="align-items-end mb-3">
        <Col xs={12} md={6} lg={4}>
          <Form.Group className="mb-2 text-start w-100">
            <Form.Label className="mb-1">Search</Form.Label>
            <div className="position-relative w-100">
              <Form.Control
                type="text"
                placeholder="Seach by title, country or grade level"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setPage(1);
                }}
                style={{ paddingRight: '35px' }}
              />
              {searchText.trim() && (
                <FaTimes
                  onClick={() => setSearchText('')}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    color: '#999',
                    fontSize: '16px',
                  }}
                />
              )}
            </div>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col>
          <Table responsive className="scribe-contents-table">
            <thead>
              <tr>
                <th>Course Title</th>
                <th>Description</th>
                <th>Country</th>
                <th>Grade Level</th>
                <th>Duration</th>
                <th>Credits</th>
                <th>Status</th>
                <th>Created On</th>
                <th>Outline</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center', padding: '4rem 0' }}>
                    <Button variant="link" className="mt-2 mb-5 btn-spinner ms-0 text-decoration-none" disabled>
                      <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" className="me-1" />
                    </Button>
                  </td>
                </tr>
              ) : courses.length > 0 ? (
                courses.map((course) => (
                  <tr key={course.course_id}>
                    <td><strong>{course.course_title}</strong></td>
                    <td style={{ maxWidth: '300px' }}>
                      {course.course_description.length > 100
                        ? `${course.course_description.substring(0, 100)}...`
                        : course.course_description}
                    </td>
                    <td>{course.country}</td>
                    <td>{course.grade_level}</td>
                    <td>{course.duration}</td>
                    <td>{course.credits}</td>
                    <td>
                      <Badge bg="success">{course.status}</Badge>
                    </td>
                    <td>{formatDate(course.created_on)}</td>
                    <td>
                      <img
                        style={{ cursor: 'pointer' }}
                        src="/images/eye-grey.svg"
                        alt="View Outline"
                        onClick={() => fetchOutline(course.course_id)}
                        title="View Outline"
                      />
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <img
                        style={{ cursor: 'pointer', marginRight: '10px' }}
                        src="/images/download-square-grey.svg"
                        alt="Import"
                        onClick={() => handleImportCourse(course)}
                        title="Import to Moodle"
                      />
                      <img
                        style={{ cursor: 'pointer' }}
                        src="/images/delete-grey.svg"
                        alt="Delete"
                        onClick={() => handleDeleteCourse(course.course_id)}
                        title="Delete Course"
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center', padding: '2rem' }}>
                    <img src="/images/no-data-found.jpeg" alt="No data" style={{ maxWidth: '400px' }} />
                    <p>No saved courses found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Col>
      </Row>

      {courses.length > 0 && !loading && (
        <Row>
          <Col xs={12} md={6} className="d-flex justify-content-start mb-2 mb-md-0">
            Displaying {courses.length} of {paginationData.total} Records
          </Col>
          <Col xs={12} md={6} className="d-flex justify-content-md-end justify-content-start">
            <Paginations
              current_page={paginationData.page}
              page_size={paginationData.per_page}
              total_page={paginationData.total_pages}
              total_records={paginationData.total}
              clicked_page={handlePageChange}
            />
          </Col>
        </Row>
      )}

      <Modal show={showOutlineModal} onHide={() => { setShowOutlineModal(false); setExpandedUnits([]); }} centered dialogClassName="modal-90w" fullscreen="md-down" style={{ zIndex: 10000 }}>
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="d-flex align-items-center gap-2">
              <Form.Control
                type="text"
                value={editCourseData.course_title}
                onChange={(e) => setEditCourseData({ ...editCourseData, course_title: e.target.value })}
                style={{ maxWidth: '500px' }}
              />
              <Button variant="primary" size="sm" onClick={handleSaveCourseEdit} disabled={editCourseLoading || editCourseData.course_title === originalCourseTitle}>
                {editCourseLoading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ overflowY: 'auto', maxHeight: '500px' }}>
          {outlineLoading ? (
            <div className="text-center py-4">
              <div style={{ width: '24px', textAlign: 'center', margin: '0 auto' }}>
                <img src="/images/loader.svg" style={{ width: '20px' }} alt="Loading" />
              </div>
            </div>
          ) : selectedOutline?.units ? (
            selectedOutline.units.map(unit => (
              <div key={unit.unit_number} className="mb-3 border rounded">
                <div 
                  className="d-flex align-items-start p-3" 
                  style={{ backgroundColor: '#f8f9fa' }}
                >
                  <div className="me-2 mt-1" style={{ cursor: 'pointer' }} onClick={() => toggleUnit(unit.unit_number)}>
                    {expandedUnits.includes(unit.unit_number) ? <FaChevronDown /> : <FaChevronRight />}
                  </div>
                  <div className="flex-grow-1" style={{ cursor: 'pointer' }} onClick={() => toggleUnit(unit.unit_number)}>
                    <h6 className="mb-1">
                      <strong>Unit {unit.unit_number}: {unit.unit_title}</strong>
                      {unit.duration_weeks && <span className="text-muted ms-2" style={{ fontSize: '0.85rem' }}>Duration: {unit.duration_weeks} weeks</span>}
                    </h6>
                    <p className="mb-0 text-muted" style={{ fontSize: '0.9rem' }}>{unit.unit_description}</p>
                  </div>
                  <FaEdit 
                    style={{ cursor: 'pointer', color: '#0d6efd', zIndex: 1, fontSize: '4rem', marginLeft: '8px' }} 
                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleEdit('unit', unit); }}
                    title="Edit Unit"
                  />
                </div>
                {expandedUnits.includes(unit.unit_number) && unit.lessons && (
                  <div className="px-3 pb-3">
                    {unit.lessons.map(lesson => (
                      <div key={lesson.lesson_number} className="border-top pt-2 mt-2">
                        <div className="mb-1 d-flex align-items-start justify-content-between">
                          <div>
                            <Badge bg="secondary" className="me-2">{unit.unit_number}.{lesson.lesson_number}</Badge>
                            <strong>{lesson.lesson_title}</strong>
                            {lesson.duration_days && <span className="text-muted ms-2" style={{ fontSize: '0.85rem' }}>Duration: {lesson.duration_days} days</span>}
                          </div>
                          <FaEdit 
                            style={{ cursor: 'pointer', color: '#0d6efd', fontSize: '1.1rem', zIndex: 1 }} 
                            onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleEdit('lesson', lesson); }}
                            title="Edit Lesson"
                          />
                        </div>
                        <div className="mb-1 text-muted" style={{ fontSize: '0.85rem', marginLeft: '60px' }} dangerouslySetInnerHTML={{ __html: lesson.lesson_description }} />
                        {lesson.standards && lesson.standards.length > 0 && (
                          <div style={{ marginLeft: '60px' }}>
                            {lesson.standards.map((std, idx) => (
                              <Badge key={idx} bg="info" className="me-1">
                                {typeof std === 'string' ? std : std.code || std.id || ''}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          ) : (
            <p className="text-center text-muted">No outline available</p>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered fullscreen="sm-down" style={{ zIndex: 10001 }} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Edit {editType === 'unit' ? 'Unit' : 'Lesson'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3 mt-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                value={editData.title}
                onChange={(e) => setEditData({ ...editData, title: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <ReactQuill
                theme="snow"
                value={editData.description}
                onChange={(value) => setEditData({ ...editData, description: value })}
                modules={quillModules}
                formats={quillFormats}
                style={{ height: '400px', marginBottom: '50px' }}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Duration ({editType === 'unit' ? 'weeks' : 'days'})</Form.Label>
              <Form.Control
                type="number"
                value={editData.duration}
                onChange={(e) => setEditData({ ...editData, duration: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={editLoading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveEdit} disabled={editLoading || (editData.title === originalEditData.title && editData.description === originalEditData.description && editData.duration === originalEditData.duration)}>
            {editLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>

      {showSelectCourseModal && (
        <Modal show={showSelectCourseModal} onHide={() => setShowSelectCourseModal(false)} centered fullscreen="sm-down" size="lg" style={{ zIndex: 10002 }}>
          <Modal.Header closeButton>
            <Modal.Title>Select Course</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Select
                options={catArray}
                placeholder="Select Category"
                isClearable
                isSearchable
                value={selectedCategory}
                onMenuOpen={getCatArray}
                noOptionsMessage={() => 'No options available'}
                onChange={(selectedOption) => setSelectedCategory(selectedOption)}
              />
            </Form.Group>
            {selectedRowCourse?.duration?.toLowerCase() === 'full year' && (
              <Form.Group className="mb-3">
                <Form.Label>Select Part</Form.Label>
                <div>
                  <Form.Check
                    inline
                    type="radio"
                    label="Part 1"
                    name="coursePart"
                    value="part 1"
                    checked={selectedPart === 'part 1'}
                    onChange={(e) => setSelectedPart(e.target.value)}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    label="Part 2"
                    name="coursePart"
                    value="part 2"
                    checked={selectedPart === 'part 2'}
                    onChange={(e) => setSelectedPart(e.target.value)}
                  />
                </div>
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSelectCourseModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSelectCourseImport} disabled={!selectedCategory}>
              Import
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {showDeleteModal && (
        <DeleteConfirmationModal
          loading={deletingCourse}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={confirmDeleteCourse}
          description="Are you sure you want to delete? This action cannot be undone."
        />
      )}

      {showImportModal && (
        <DeleteConfirmationModal
          loading={importingCourse}
          onClose={() => setShowImportModal(false)}
          onConfirm={confirmImportCourse}
          description="Are you sure you want to import this course to Moodle?"
        />
      )}
    </Container>
  );
};

export default SavedCourses;
